import { ExtraPermissionPersistedSchema, UserRole } from '../codecs/tdc';
import { Permission } from './permissions';

type AndPermission = { and: Permissions[] };
function isAndPermission(perm: any): perm is AndPermission {
  return perm.and !== undefined;
}

type OrPermission = { or: Permissions[] };
function isOrPermission(perm: any): perm is OrPermission {
  return perm.or !== undefined;
}

/**
 * @deprecated use Perms
 */
export type Permissions = Permission | AndPermission | OrPermission;

// The name Permission collide with a build-in type
export type Perms = Permission | AndPermission | OrPermission;

export type UserInfo = {
  role: UserRole;
  extraPermissions: ExtraPermissionPersistedSchema[];
};

/**
 * Should likely be called `hasPermission`
 *
 * This only asserts if a User - Dealership relation has a specific ID.
 * It does not assert if the resource the user is working on belongs to
 * that dealership.
 *
 * @param permissions
 * @param ud
 * @returns
 */
export const permissionChecker = (
  permissions: Perms,
  user?: UserInfo
): boolean => {
  if (!user) {
    return false;
  }
  if (isAndPermission(permissions)) {
    return permissions.and.reduce(
      (p, c) => p && permissionChecker(c, user),
      true
    );
  } else if (isOrPermission(permissions)) {
    return permissions.or.reduce(
      (p, c) => p || permissionChecker(c, user),
      false
    );
  } else {
    return checkPermission(permissions, user);
  }
};

/**
 * Checks permission
 *
 * Permission matrix here: https://www.notion.so/Authentication-and-Auth-per-role-Matrix-67937c53c9f94d238019d3d6ef980241
 *
 * @param perm
 * @param ud
 * @returns
 */
const checkPermission = (perm: Perms, ud: UserInfo): boolean => {
  let positiveRoles: UserRole[] = [];
  let negativeRoles: UserRole[] = [];

  // This function is only for staff.
  if (ud.role === 'CUSTOMER') {
    return false;
  }

  switch (perm) {
    case 'assignStaff':
      negativeRoles = ['CUSTOMER'];
      return !negativeRoles.includes(ud.role);

    case 'uploadFileToTransction':
      negativeRoles = ['BDC', 'CUSTOMER', 'ADMIN'];
      return !negativeRoles.includes(ud.role);

    case 'viewAllTransactions':
      positiveRoles = ['SALES_MANAGER', 'ADMIN', 'BDC', 'FNI_MANAGER'];
      return positiveRoles.includes(ud.role);

    case 'createTransction':
      positiveRoles = [
        'FNI_MANAGER',
        'BDC',
        'SALES_MANAGER',
        'SALES_PERSON',
        'ADMIN'
      ];
      return positiveRoles.includes(ud.role);

    // For Sales Person & F&I Manager can update transaction
    // But only for assigned transaction
    // This checks is done in the auth resolver
    case 'updateTransaction':
      positiveRoles = ['BDC', 'SALES_MANAGER', 'ADMIN'];
      return positiveRoles.includes(ud.role);

    case 'pushHardCreditApplication':
      positiveRoles = ['FNI_MANAGER', 'SALES_MANAGER', 'ADMIN'];
      return positiveRoles.includes(ud.role);

    // BDC is Added here because it is already have a permission for `viewAllTransactions`
    case 'viewAllCustomers':
      positiveRoles = [
        'FNI_MANAGER',
        'SALES_MANAGER',
        'SALES_PERSON',
        'ADMIN',
        'BDC'
      ];
      return positiveRoles.includes(ud.role);

    case 'printDealerJacket':
      positiveRoles = ['ADMIN', 'FNI_MANAGER', 'SALES_MANAGER', 'SALES_PERSON'];
      return positiveRoles.includes(ud.role);

    case 'editCustomerDatabaseProfile':
      positiveRoles = ['ADMIN', 'SALES_MANAGER'];
      return positiveRoles.includes(ud.role);

    case 'viewCreditReports':
      negativeRoles = ['BDC', 'CUSTOMER', 'SALES_PERSON'];
      return !negativeRoles.includes(ud.role);

    case 'downloadDealJacket':
      negativeRoles = ['BDC', 'SALES_PERSON'];
      return !negativeRoles.includes(ud.role);

    case 'viewDealJacket':
      negativeRoles = ['BDC'];
      return !negativeRoles.includes(ud.role);

    case 'manageUsers':
      positiveRoles = ['ADMIN', 'SALES_MANAGER'];
      return positiveRoles.includes(ud.role);

    case 'pushFileToCdkDealJacket':
      positiveRoles = ['ADMIN', 'FNI_MANAGER', 'SALES_MANAGER'];
      return positiveRoles.includes(ud.role);

    case 'groupViewAllDealerships':
      positiveRoles = ['ADMIN'];
      return positiveRoles.includes(ud.role);

    case 'requestSignatures':
      negativeRoles = ['BDC'];
      return !negativeRoles.includes(ud.role);

    // See: REQ-TUSS-007
    case 'viewReports':
      negativeRoles = ['CUSTOMER'];
      return !negativeRoles.includes(ud.role);

    // Do far, we let all staff view referrals
    case 'viewReferrals':
      negativeRoles = ['CUSTOMER'];
      return !negativeRoles.includes(ud.role);

    // Can override ALL tasks
    case 'canOverrideTasks':
      return ud.extraPermissions?.includes('OVERRIDE_TASKS') ?? false;

    // TODO: Fix this,
    // This is not representative, this is only a temporary entry for when I moved to ensure
    // completeness of this switch case
    case 'triggerSignPostPurchase':
      positiveRoles = ['ADMIN'];
      return positiveRoles.includes(ud.role);

    // TODO: Fix this,
    // This is not representative, this is only a temporary entry for when I moved to ensure
    // completeness of this switch case
    case 'triggerSignPrePurchase':
      positiveRoles = ['ADMIN'];
      return positiveRoles.includes(ud.role);

    case 'unlockTransactions':
      positiveRoles = ['ADMIN'];
      return positiveRoles.includes(ud.role);

    case 'editLendingPortalData':
      positiveRoles = ['FNI_MANAGER', 'SALES_MANAGER', 'ADMIN'];
      return positiveRoles.includes(ud.role);

    default:
      // The two case should never happen, they are here for typing
      if ('and' in perm) {
        return false;
      }
      if ('or' in perm) {
        return false;
      }
      return assertUnreachable(perm);
  }
};

const assertUnreachable = (x: never): never => {
  throw new Error("Didn't expect to get here");
};
